/**
 * Custom hook for CDDRecords data filtering and pagination
 */

import { useState } from 'react';

export interface CDDRecord {
  id: string;
  reference: string;
  customer: string;
  abn: string;
  type: string;
  riskLevel: 'low' | 'medium' | 'high';
  status: 'completed' | 'in-progress' | 'failed' | 'not-started' | 'pending';
  reason?: string;
}

export interface CDDFilters {
  search: string;
  status: 'all' | 'completed' | 'in-progress' | 'failed' | 'not-started' | 'pending';
  type: 'all' | 'Company' | 'Trust' | 'Individual';
  risk: 'all' | 'low' | 'medium' | 'high';
}

const PAGE_SIZE = 20;

export const useCDDFilters = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<CDDFilters['status']>('all');
  const [typeFilter, setTypeFilter] = useState<CDDFilters['type']>('all');
  const [riskFilter, setRiskFilter] = useState<CDDFilters['risk']>('all');
  const [appliedFilters, setAppliedFilters] = useState<CDDFilters>({
    search: '',
    status: 'all',
    type: 'all',
    risk: 'all',
  });
  const [currentPage, setCurrentPage] = useState(1);

  const applyFilters = () => {
    setAppliedFilters({ search: searchTerm, status: statusFilter, type: typeFilter, risk: riskFilter });
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setTypeFilter('all');
    setRiskFilter('all');
    setAppliedFilters({ search: '', status: 'all', type: 'all', risk: 'all' });
    setCurrentPage(1);
  };

  const getFilteredData = (data: CDDRecord[]): CDDRecord[] =>
    data.filter((record) => {
      const matchesSearch =
        appliedFilters.search === '' ||
        (record.customer || '').toLowerCase().includes(appliedFilters.search.toLowerCase()) ||
        (record.reference || '').toLowerCase().includes(appliedFilters.search.toLowerCase()) ||
        (record.abn || '').toLowerCase().includes(appliedFilters.search.toLowerCase());

      const matchesStatus =
        appliedFilters.status === 'all' ||
        record.status === appliedFilters.status ||
        (appliedFilters.status === 'pending' && record.status === 'in-progress');
      const matchesType = appliedFilters.type === 'all' || record.type === appliedFilters.type;
      const matchesRisk = appliedFilters.risk === 'all' || record.riskLevel === appliedFilters.risk;

      return matchesSearch && matchesStatus && matchesType && matchesRisk;
    });

  const getPaginatedData = (filteredData: CDDRecord[]): CDDRecord[] => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredData.slice(start, start + PAGE_SIZE);
  };

  const getTotalPages = (totalFiltered: number) => Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));

  return {
    searchTerm, setSearchTerm,
    statusFilter, setStatusFilter,
    typeFilter, setTypeFilter,
    riskFilter, setRiskFilter,
    appliedFilters, setAppliedFilters,
    applyFilters,
    resetFilters,
    getFilteredData,
    getPaginatedData,
    getTotalPages,
    currentPage,
    setCurrentPage,
    PAGE_SIZE,
  };
};

export const getRiskIcon = (risk: string): string => {
  switch (risk) {
    case 'low': return '🟢';
    case 'medium': return '🟡';
    case 'high': return '🔴';
    default: return '-';
  }
};

export const getStatusIcon = (status: string): string => {
  switch (status) {
    case 'completed': return '✅';
    case 'in-progress': return '🔄';
    case 'pending': return '⏳';
    case 'not-started': return '⚪';
    case 'failed': return '❌';
    default: return '';
  }
};

export const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'active': return 'Active';
    case 'completed': return 'Completed';
    case 'pending': return 'Pending';
    case 'pending-review': return 'Pending Review';
    case 'in-progress': return 'In Progress';
    case 'not-started': return 'Not Started';
    case 'failed': return 'Withdrawn';
    default: return status;
  }
};
